import { startOfDay, addMinutes, isBefore, format, parse, isAfter, isSameDay } from 'date-fns';
import { QUIET_HOURS } from '../constants/scheduling';
import { fromZonedTime } from 'date-fns-tz';
/**
 * Formats a 24h time string (HH:mm) to 12h format with AM/PM
 * e.g., "14:30" -> "2:30 PM"
 */
export const formatTimeDisplay = (time: string): string => {
  const date = parse(time, 'HH:mm', new Date());
  return format(date, 'h:mm a');
};

interface TimeSlot {
  value: string;
  disabled: boolean;
}

/**
 * Generates time slots in 30-minute increments for a 24-hour period
 * Returns array of time slots with disabled status for past times if date is today
 */
export function generateTimeSlots(date?: string): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const now = new Date();
  const selectedDate = date ? parse(date, 'yyyy-MM-dd', new Date()) : undefined;
  const isToday = selectedDate && isSameDay(selectedDate, now);
  const start = startOfDay(new Date());
  const end = addMinutes(start, 24 * 60); // 24 hours in minutes
  let current = start;

  while (isBefore(current, end)) {
    const timeString = format(current, 'HH:mm');
    const isDisabled = isToday && isBefore(current, now);

    slots.push({
      value: timeString,
      disabled: isDisabled ?? false
    });

    current = addMinutes(current, 30);
  }

  return slots;
}

/**
 * Checks if a given time falls within quiet hours (8 PM - 9 AM)
 * Used for FTC compliance in SMS scheduling
 */
export const isQuietHours = (time: string): boolean => {
  if (!time) return false;
  const [hours] = time.split(':').map(Number);
  return hours >= QUIET_HOURS.START || hours < QUIET_HOURS.END;
};

/**
 * Validates if the scheduled date/time is valid (not in the past)
 */
export const isValidScheduleTime = (date: Date | undefined, time: string): boolean => {
  if (!date || !time) return false;
  const today = startOfDay(new Date());
  return !isBefore(date, today);
};

/**
 * Validates if a given date and time combination is valid (not in the past)
 * @param date The date to validate
 * @param time The time in HH:mm format
 * @returns boolean indicating if the date/time is valid
 */
export const isValidDateTime = (date: Date, time: string): boolean => {
  const [hours, minutes] = time.split(':').map(Number);
  const scheduledDateTime = new Date(date);
  scheduledDateTime.setHours(hours, minutes);

  return isAfter(scheduledDateTime, new Date());
};

/**
 * Converts a "YYYY-MM-DD" + "HH:mm" + "America/New_York"
 * (or any IANA timezone) => UTC ISO string
 */
export function getUTCISOString(
  dateStr: string, // e.g. "2024-03-19"
  timeStr: string, // e.g. "14:30"
  timezone: string // e.g. "America/New_York"
): string {
  const localDateTime = `${dateStr} ${timeStr}:00`;

  const utcDate = fromZonedTime(localDateTime, timezone);

  return utcDate.toISOString();
}
